import { ROLES, papelDeAcesso } from '../contexts/AuthContext';

/**
 * Conteúdo do guia de uso do SIDEB.
 *
 * Por que existe: o sistema está no ar e a frequência mudou de forma — ela
 * deixou de ser lançada para o dia inteiro e passou a ser lançada POR AULA
 * (disciplina + horário). Quem já usava precisa saber disso antes de abrir
 * a tela, ou lança errado; quem chega agora precisa saber por onde começar.
 *
 * Quando abre sozinho: só no primeiro acesso de cada pessoa (quem nunca
 * concluiu o guia; `CustomUser.guia_visto_versao` vazio). Depois disso ele
 * fica no botão "?". Subir a versão NÃO reabre o guia para quem já o viu —
 * a versão só registra qual edição a pessoa concluiu.
 *
 * Cada capítulo tem `allowedRoles` (ausente = todo mundo vê). A ordem aqui
 * é a ordem em que aparecem, e o capítulo do professor vem primeiro para
 * quem é professor, porque é quem lança no dia a dia.
 */

export const GUIA_VERSAO = '2026.09-substituicao-e-dia-letivo';

export const GUIA_TITULO = 'Guia rápido do SIDEB';

const TODOS = [
  ROLES.ADMIN, ROLES.COORDENADOR, ROLES.SECRETARIO, ROLES.SUPERVISOR,
  ROLES.REFERENCIA_TECNICA, ROLES.PROFESSOR, ROLES.GESTOR,
];

/**
 * Um capítulo do guia.
 *
 * - `titulo`   — aparece no cabeçalho e na lista lateral
 * - `resumo`   — uma frase, mostrada logo abaixo do título
 * - `blocos`   — o corpo, na ordem. Tipos: 'texto', 'passos', 'aviso',
 *                'atencao', 'caminho' e 'legenda'
 * - `allowedRoles` — quem vê este capítulo
 */
export const guiaCapitulos = [
  // ───────────────────────────────────────────────────────────────────
  // PROFESSOR — o capítulo mais detalhado, porque é o uso diário.
  // ───────────────────────────────────────────────────────────────────
  {
    id: 'professor-onde-comeco',
    titulo: 'Por onde começar',
    icone: '🧭',
    resumo: 'Dois caminhos até a sua aula: a Grade Horária e o Diário.',
    allowedRoles: [ROLES.PROFESSOR],
    blocos: [
      {
        tipo: 'texto',
        texto:
          'Tudo o que você lança no dia a dia — aula, frequência e notas — '
          + 'está no Diário de Turma. Há dois jeitos de chegar lá, e o mais '
          + 'rápido é pela Grade Horária.',
      },
      {
        tipo: 'caminho',
        titulo: 'O caminho curto (recomendado)',
        passos: ['Menu', 'Diário', 'Grade Horária', 'clique na sua aula'],
      },
      {
        tipo: 'texto',
        texto:
          'A Grade Horária mostra o quadro da semana. Clicando na célula da '
          + 'sua aula, o sistema abre a frequência daquela aula, já na data '
          + 'certa — sem você precisar procurar o dia no calendário.',
      },
      {
        tipo: 'caminho',
        titulo: 'O caminho completo',
        passos: ['Menu', 'Diário', 'Diário de Turma', 'escolha a aba'],
      },
      {
        tipo: 'aviso',
        texto:
          'Se você dá aula em uma turma só, os atalhos “Diário de Turma” e '
          + '“Grade Horária” já entram nela direto, sem pedir para escolher.',
      },
      {
        tipo: 'legenda',
        titulo: 'As abas do Diário de Turma',
        itens: [
          { rotulo: '📝 Registro de Aulas', texto: 'Cadastrar a aula: conteúdo dado, habilidades da BNCC, tipo de aula.' },
          { rotulo: '📅 Frequência', texto: 'A chamada: escolhe a data, escolhe a aula e lança. Não precisa registrar a aula antes.' },
          { rotulo: '📊 Planilha', texto: 'A frequência do mês inteiro da turma, em forma de tabela.' },
          { rotulo: '🎯 Notas', texto: 'As notas dos quatro bimestres e a prova final.' },
        ],
      },
    ],
  },

  {
    id: 'professor-cadastrar-aula',
    titulo: 'Cadastrar uma aula',
    icone: '📝',
    resumo: 'Primeiro a aula existe, depois ela recebe a chamada.',
    allowedRoles: [ROLES.PROFESSOR],
    blocos: [
      {
        tipo: 'atencao',
        texto:
          'A ordem importa: a frequência pertence a uma aula. Registre a aula '
          + 'primeiro — sem ela, não há onde lançar a chamada.',
      },
      {
        tipo: 'passos',
        titulo: 'Passo a passo',
        itens: [
          'Abra o Diário de Turma e fique na aba “Registro de Aulas”.',
          'Escolha a disciplina no seletor do topo. A tela só mostra as aulas dessa disciplina.',
          'Clique em “Nova Aula” e escolha a data.',
          'Escolha o tipo de aula. Ele vem antes do horário de propósito: é o tipo que decide quais horários a lista vai oferecer.',
          'Escolha o horário. A lista traz as SUAS aulas naquela turma e naquele dia — se você tem duas, aparecem as duas. Escolher o horário é o que amarra a chamada à aula certa.',
          'Preencha o conteúdo ministrado e as habilidades da BNCC.',
          'Salve. Ao salvar uma aula nova, o sistema já abre a frequência dela para você lançar na hora.',
        ],
      },
      {
        tipo: 'atencao',
        titulo: 'Cobriu a aula de um colega?',
        texto:
          'Escolha o tipo “Extra / Substituição”. A lista de horários passa a '
          + 'mostrar também os horários daquele dia que ainda estão '
          + 'descobertos, dizendo de quem é cada um — “Substituindo · 3ª aula • '
          + '09:00-09:50 — História · Diego Alves”. Escolhendo um deles, a aula '
          + 'fica registrada como sua e o horário do colega deixa de aparecer '
          + 'como pendente no acompanhamento. Horário que o colega já lançou não '
          + 'aparece: ele não faltou, não há o que cobrir.',
      },
      {
        tipo: 'legenda',
        titulo: 'Situações que aparecem com frequência',
        itens: [
          {
            rotulo: 'Permuta',
            texto:
              'Você deu a sua disciplina no horário que a grade reserva para outra. '
              + 'Pode registrar normalmente — o sistema aceita e mostra um aviso roxo. '
              + 'No acompanhamento, o horário aparece como cumprido, com a disciplina '
              + 'prevista ao lado da que aconteceu.',
          },
          {
            rotulo: 'Aula não realizada',
            texto:
              'A aula não aconteceu (formação, sala interditada, falta). Escolha o tipo '
              + '“Aula não realizada” e escreva o motivo. O horário sai da cobrança em '
              + 'vez de ficar pendente para sempre, e conteúdo e BNCC deixam de ser '
              + 'exigidos. Uma aula assim não tem chamada a lançar.',
          },
          {
            rotulo: 'Reposição e Extra / Substituição',
            texto:
              'Aula fora da grade, ou aula que você cobriu de um colega. Registre com o '
              + 'tipo correspondente — ela conta como cumprida no acompanhamento. São '
              + 'também os dois únicos tipos aceitos em dia sem aula.',
          },
          {
            rotulo: 'Dia sem aula',
            texto:
              'Feriado, recesso, ponto facultativo cadastrado no Calendário Letivo ou '
              + 'fim de semana: a aula comum é recusada e a tela diz por quê. Sábado '
              + 'letivo é o caso de usar Reposição.',
          },
          {
            rotulo: 'A mesma aula duas vezes',
            texto:
              'Cada horário recebe um registro por dia. Se a aula já existe, edite a que '
              + 'está lá em vez de criar outra — duas aulas no mesmo horário fazem o '
              + 'acompanhamento ler só uma delas.',
          },
        ],
      },
    ],
  },

  {
    id: 'professor-frequencia',
    titulo: 'Lançar a frequência',
    icone: '📋',
    resumo: 'A chamada é de uma aula: disciplina e horário.',
    allowedRoles: [ROLES.PROFESSOR],
    blocos: [
      {
        tipo: 'passos',
        titulo: 'Passo a passo',
        itens: [
          'No Diário de Turma, abra a aba “Frequência”. (Vindo da Grade Horária, você já cai aqui, na aula certa.)',
          'No calendário à esquerda, clique na data. Os dias que já têm chamada ficam em verde.',
          'Em “Aula”, escolha qual aula você está lançando. A aula não precisa estar registrada antes: se ainda não estiver, entra no diário ao salvar a chamada.',
          'A lista de alunos abre com todos marcados como Presente. Marque quem faltou, chegou atrasado ou tem falta justificada.',
          'Salve.',
        ],
      },
      {
        tipo: 'legenda',
        titulo: 'Os quatro status',
        itens: [
          { rotulo: 'P — Presente', texto: 'Assistiu à aula.' },
          { rotulo: 'F — Ausente', texto: 'Faltou.' },
          { rotulo: 'J — Justificado', texto: 'Faltou, com justificativa registrada. A justificativa fica ao lado da falta no painel, mas a lei não abona: ela continua contando nos 75%.' },
          { rotulo: 'A — Atrasado', texto: 'Chegou atrasado. Conta como presença.' },
        ],
      },
      {
        tipo: 'atencao',
        titulo: 'Quando ninguém faltou',
        texto:
          'A tela abre com a turma inteira marcada como Presente. Salvar sem '
          + 'conferir é indistinguível de uma chamada real em que ninguém faltou '
          + '— e isso já apagou falta de verdade lançada por outra tela. Por '
          + 'isso, ao salvar uma chamada sem nenhuma falta, o sistema pede que '
          + 'você confirme que realmente ninguém faltou. Confirmando, a chamada '
          + 'vale como qualquer outra.',
      },
      {
        tipo: 'texto',
        texto:
          'Não dá para lançar frequência em data futura, nem em feriado '
          + 'nacional. O dia aparece bloqueado no calendário, com o motivo.',
      },
      {
        tipo: 'aviso',
        titulo: 'Lançou na turma errada?',
        texto:
          'Dá para limpar a chamada de uma aula sua — a aula continua '
          + 'registrada, só a frequência sai. Antes o único jeito de corrigir '
          + 'era apagar a aula inteira, levando o conteúdo junto.',
      },
      {
        tipo: 'aviso',
        texto:
          'O resumo do dia do aluno é recalculado sozinho a cada chamada que '
          + 'você salva. Se ele esteve presente em alguma aula, o dia conta '
          + 'como presença. Você não precisa lançar nada além das suas aulas.',
      },
    ],
  },

  {
    id: 'professor-notas',
    titulo: 'Notas e acompanhamento',
    icone: '🎯',
    resumo: 'Notas por bimestre, recuperação e a sua lista de pendências.',
    allowedRoles: [ROLES.PROFESSOR],
    blocos: [
      {
        tipo: 'passos',
        titulo: 'Notas',
        itens: [
          'Diário de Turma → aba “Notas”.',
          'Escolha a disciplina e o ano letivo.',
          'Lance as três notas de cada bimestre. As médias são calculadas automaticamente.',
          'As notas salvam sozinhas alguns segundos depois que você para de digitar — não há botão de salvar.',
          'A recuperação (RP) substitui a avaliação correspondente quando a nota é maior.',
        ],
      },
      {
        tipo: 'passos',
        titulo: 'Meu Painel',
        itens: [
          'Menu → Diário → Meu Painel.',
          'Mostra, das suas turmas: as aulas que faltam registrar, as que estão sem chamada, a frequência e as notas.',
          'É o jeito mais rápido de ver se ficou alguma pendência do mês.',
        ],
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────
  // COORDENAÇÃO / GESTÃO
  // ───────────────────────────────────────────────────────────────────
  {
    id: 'gestao-grade',
    titulo: 'Montar a grade horária',
    icone: '🕘',
    resumo: 'A grade é a base de tudo: sem ela, o professor não lança aula.',
    allowedRoles: [ROLES.ADMIN, ROLES.COORDENADOR, ROLES.REFERENCIA_TECNICA],
    blocos: [
      {
        tipo: 'atencao',
        texto:
          'A grade define qual disciplina ocupa cada horário de cada turma, e '
          + 'é dela que sai a lista de aulas do professor, já com período e '
          + 'horário. Ela não é pré-requisito da chamada: sem grade montada, o '
          + 'professor lança a aula assim mesmo, como aula avulsa. Com a '
          + 'grade, o registro fica no lugar certo.',
      },
      {
        tipo: 'passos',
        titulo: 'Passo a passo',
        itens: [
          'Menu → Diário → Grade Horária (ou Turmas → a turma → Grade Horária).',
          'Defina quantas aulas a turma tem por dia.',
          'Clique em cada célula do quadro e escolha a disciplina e o professor.',
          'Salve. A partir daí o professor daquela disciplina passa a ver e lançar aquelas aulas.',
        ],
      },
      {
        tipo: 'aviso',
        texto:
          'Professor e Supervisor conseguem consultar a grade, mas só '
          + 'Administrador, Coordenador e Referência Técnica podem editá-la.',
      },
      {
        tipo: 'atencao',
        titulo: 'Só aparecem dois ou três professores na lista da célula?',
        texto:
          'A lista de professores da célula vem da LOTAÇÃO: quem está '
          + 'vinculado àquela escola em RH → Funcionários. Professor recém-'
          + 'cadastrado sem a escola marcada não aparece, mesmo existindo no '
          + 'sistema. A tela avisa quando nenhum professor está lotado na '
          + 'escola da turma — nesse caso, abra o cadastro dele em RH → '
          + 'Funcionários e marque a escola.',
      },
    ],
  },

  {
    id: 'gestao-aluno-que-saiu',
    titulo: 'Aluno que saiu da turma',
    icone: '🎒',
    resumo: 'Como tirar da caderneta quem foi transferido — e o que fazer '
      + 'quando não se sabe para onde ele foi.',
    allowedRoles: [ROLES.ADMIN, ROLES.COORDENADOR, ROLES.SECRETARIO, ROLES.REFERENCIA_TECNICA],
    blocos: [
      {
        tipo: 'texto',
        texto:
          'Menu → Alunos → procure o aluno → Editar → campo Status. É o status '
          + 'que tira o aluno da caderneta: mudou o status, ele sai da chamada '
          + 'da professora no mesmo instante.',
      },
      {
        tipo: 'legenda',
        titulo: 'Qual status usar',
        itens: [
          { rotulo: 'Transferido', texto: 'Ele foi para outra escola e você sabe qual. Sai da caderneta.' },
          { rotulo: 'Vacante (sem turma)', texto: 'Ele saiu da turma e ainda não se sabe para onde foi. Sai da caderneta e entra na lista de vacantes, para ser remanejado quando a escola de destino aparecer.' },
          { rotulo: 'Desistente', texto: 'Abandonou os estudos. Sai da caderneta.' },
          { rotulo: 'Concluído', texto: 'Terminou a etapa. Sai da caderneta.' },
        ],
      },
      {
        tipo: 'aviso',
        texto:
          'O que ele já tinha lançado não se perde: nos meses em que houve '
          + 'chamada ou nota dele, ele continua aparecendo no diário e na '
          + 'planilha. O que muda é a chamada daqui para a frente.',
      },
      {
        tipo: 'aviso',
        texto:
          'O aluno vacante continua ligado à última turma de propósito — é '
          + 'assim que você o encontra depois ("era do 1º ano do Estevão"). O '
          + 'quadro "Vacantes", no topo da tela de Alunos, filtra a lista para '
          + 'eles com um clique.',
      },
    ],
  },

  {
    id: 'gestao-acompanhamento',
    titulo: 'Acompanhar os diários',
    icone: '🔎',
    resumo: 'Aulas previstas contra aulas lançadas, dia a dia.',
    allowedRoles: [ROLES.ADMIN, ROLES.COORDENADOR, ROLES.SECRETARIO, ROLES.SUPERVISOR, ROLES.REFERENCIA_TECNICA],
    blocos: [
      {
        tipo: 'texto',
        texto:
          'Menu → Diário → Acompanhamento de Diários. A tela compara o que a '
          + 'grade previa com o que os professores lançaram, e diz exatamente '
          + 'o que falta e de quem é.',
      },
      {
        tipo: 'legenda',
        titulo: 'O que cada estado significa',
        itens: [
          { rotulo: 'OK', texto: 'Aula registrada e chamada lançada. Nada a cobrar.' },
          { rotulo: 'Substituição', texto: 'O horário foi cumprido por outra disciplina (permuta). Conta como cumprido.' },
          { rotulo: 'Extra', texto: 'Aula lançada fora da grade (reposição). Conta como cumprida.' },
          { rotulo: 'Sem frequência', texto: 'A aula foi registrada, mas falta a chamada. Em aberto.' },
          { rotulo: 'Pendente', texto: 'Nenhum registro na aula. Em aberto.' },
          { rotulo: 'Não realizada', texto: 'A aula não aconteceu e o motivo está registrado. Sai da cobrança.' },
          { rotulo: 'Futura', texto: 'A data ainda não chegou.' },
        ],
      },
      {
        tipo: 'passos',
        titulo: 'Também aqui',
        itens: [
          'Filtros por escola, turma, disciplina, professor e turno.',
          'Quadro "Grade horária das turmas": quantas turmas têm a grade completa, incompleta ou nenhuma. Clique num total para ver as turmas, as aulas de cada dia e abrir a grade.',
          'Lista plana de pendências, para cobrar de uma vez.',
          'Exportação das pendências em planilha.',
          'Calendário Letivo: os dias marcados ali não geram aula esperada nenhuma — use-o para feriados municipais e recessos, senão eles aparecem como pendência.',
        ],
      },
      {
        tipo: 'aviso',
        texto:
          'O Painel da Rede (Dashboard → aba Diário, ou Menu → Diário → Painel da Rede) '
          + 'reúne frequência, notas, aulas e os alunos em risco da rede inteira numa tela só.',
      },
    ],
  },

  // ───────────────────────────────────────────────────────────────────
  {
    id: 'acessos',
    titulo: 'Seu acesso',
    icone: '🔑',
    resumo: 'Uma senha por pessoa, e troca de perfil sem sair do sistema.',
    allowedRoles: TODOS,
    blocos: [
      {
        tipo: 'texto',
        texto:
          'Quem acumula funções — por exemplo, dá aula e também coordena — '
          + 'tem um acesso para cada função, mas uma senha só. Ao entrar, o '
          + 'sistema pergunta com qual perfil você quer trabalhar.',
      },
      {
        tipo: 'passos',
        titulo: 'Trocar de perfil sem sair',
        itens: [
          'Clique no seu nome, no rodapé do menu lateral.',
          'Escolha o outro perfil na lista.',
          'A tela recarrega já no perfil escolhido.',
        ],
      },
      {
        tipo: 'aviso',
        texto:
          'Trocou de senha? A troca vale para todos os seus acessos — a senha '
          + 'é da pessoa, não do perfil.',
      },
    ],
  },

  {
    id: 'ajuda',
    titulo: 'Onde encontrar este guia depois',
    icone: '💡',
    resumo: 'Ele não some. Fica no botão “?”.',
    allowedRoles: TODOS,
    blocos: [
      {
        tipo: 'texto',
        texto:
          'Este guia fica sempre disponível no botão “?” do canto inferior '
          + 'direito, em qualquer tela do sistema. Abra quando bater a dúvida — '
          + 'você pode ir direto ao capítulo que interessa pela lista lateral.',
      },
      {
        tipo: 'aviso',
        texto:
          'Ele abre sozinho só no seu primeiro acesso. Depois disso, é só '
          + 'clicar no “?” quando precisar.',
      },
    ],
  },
];

/**
 * Capítulos visíveis para um usuário, na ordem em que devem aparecer.
 *
 * O capítulo do professor vem primeiro quando quem
 * está lendo é professor: é o uso diário dele, e enterrá-lo embaixo dos
 * capítulos de gestão faria a pessoa desistir antes de chegar lá.
 */
export function capitulosPara(user) {
  const role = papelDeAcesso(user?.role);
  if (!role) return [];
  return guiaCapitulos.filter(
    (cap) => !cap.allowedRoles || cap.allowedRoles.includes(role)
  );
}
